import './utils/gesture-handler';
import { NativeWindStyleSheet, useColorScheme } from 'nativewind';
import { StatusBar } from 'expo-status-bar';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import './assets/locales/i18n';
import {
  DefaultTheme,
  NavigationContainer,
  useNavigationContainerRef,
} from '@react-navigation/native';
import React, { useEffect, useRef, useState } from 'react';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import ErrorBoundary from 'react-native-error-boundary';
import { ActionSheetProvider } from '@expo/react-native-action-sheet';
import * as Linking from 'expo-linking';
import { LogBox } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import MainNavigator from './navigation/MainNavigator';
import linkingConfig from './navigation/linkingConfig';
import { ErrorScreen, SplashScreen } from './screens';
import { MessageProvider } from './contexts/Messages/MessageContext';
import { AppDataLoader, ToastMessage } from './components';
import { TagProvider } from './contexts/Tag/TagContext';
import { ModuleProvider } from './contexts/Module/ModuleContext';
import { AuthProvider } from './contexts/AuthContext/AuthContext';
import AuthGuard from './guards/AuthGuard';
import { NotificationProvider } from './contexts/Notification/NotificationContext';
import { PushNotificationProvider } from './contexts/PushNotification/PushNotificationContext';
import { AdminProvider } from './contexts/Admin/AdminContext';
import { AdminHelpdeskProvider } from './contexts/Admin/AdminHelpdeskContext';
import { ExtremeEventProvider } from './contexts/ExtremeEvent/ExtremeEventContext';
import { ArticlesProvider } from './contexts/ArticlesParams/ArticlesParamsContext';
import { EventsProvider } from './contexts/EventsParams/EventsParamsContext';
import { HelpdeskProvider } from './contexts/HelpdeskContext/HelpdeskContext';
import { StackProvider } from './contexts/NavigationContext/NavigationContextStack';
import { ErrorType, Language } from './resources/interfaces';
import { setupMaestro } from './maestro/api/setupMaestroMock';
import { isNative } from './utils/responsive';
import analytics from './utils/analytics';
import crashlytics from './utils/crashlytics';

// Ignore expo notifications warning in test mode
if (process.env.EXPO_PUBLIC_USE_MOCK === 'true') {
  LogBox.ignoreLogs(['expo-notifications']);
}

setupMaestro();

NativeWindStyleSheet.setOutput({
  default: 'native',
});

export default function App() {
  const { colorScheme } = useColorScheme();

  const [showSplashScreen, setShowSplashScreen] = useState<boolean>(true);

  const onAppLoadingReady = () => {
    setShowSplashScreen(false);
  };

  const navigationRef = useNavigationContainerRef();
  const routeNameRef = useRef<string | null>(null);
  const screenViewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // App opened via URL
    const handleInitialUrl = async () => {
      try {
        const url = await Linking.getInitialURL();
        if (isNative) {
          if (url) {
            await analytics().logEvent('app_opened', { open_type: 'deep_link', url });
          } else {
            await analytics().logEvent('app_opened', { open_type: 'normal' });
          }
        }
      } catch (e) {
        console.error(e);
      }
    };

    handleInitialUrl();

    // Log the user's chosen app language (falls back to the default if never
    // changed)
    const logAppLanguage = async () => {
      const savedLanguage =
        (await AsyncStorage.getItem('language').catch(() => null)) || Language.en;
      analytics()
        .setUserProperty('app_language', savedLanguage)
        .catch((e) => console.error(e));
    };

    logAppLanguage();

    // App already running - receives URL
    const linkingSub = Linking.addEventListener('url', async ({ url }) => {
      if (isNative) {
        try {
          await analytics().logEvent('app_opened', { open_type: 'deep_link', url });
        } catch (e) {
          console.error(e);
        }
      }
    });
    return () => linkingSub.remove();
  }, []);

  return (
    <SafeAreaProvider>
      <ErrorBoundary
        onError={(error, stackTrace) => {
          // ErrorBoundary swallows render crashes before they reach Crashlytics'
          // global handler, so report them manually as non-fatals.
          crashlytics().log(stackTrace);
          crashlytics().recordError(error);
        }}
        FallbackComponent={() => (
          <ErrorScreen errorStackRoute={{ type: ErrorType.internal_server_error }} />
        )}
      >
        <GestureHandlerRootView
          className="flex-1"
          style={{
            backgroundColor: colorScheme === 'light' ? '#f6f6f6' : '#101010',
          }}
        >
          <AuthProvider>
            <AuthGuard onLogin={() => setShowSplashScreen(true)}>
              <MessageProvider>
                <ModuleProvider>
                  <TagProvider>
                    <NotificationProvider>
                      <ExtremeEventProvider>
                        <ArticlesProvider>
                          <EventsProvider>
                            <HelpdeskProvider>
                              <AdminProvider>
                                <AdminHelpdeskProvider>
                                  <StatusBar
                                    backgroundColor={
                                      colorScheme === 'light' ? '#f6f6f6' : '#0b0b0b'
                                    }
                                    style={colorScheme === 'light' ? 'dark' : 'light'}
                                  />
                                  {showSplashScreen ? (
                                    <>
                                      <ToastMessage fontsLoaded={false} />
                                      <SplashScreen />
                                      <AppDataLoader onAppLoadingReady={onAppLoadingReady} />
                                    </>
                                  ) : (
                                    <>
                                      <ToastMessage />
                                      <NavigationContainer
                                        linking={linkingConfig}
                                        ref={navigationRef}
                                        onReady={() => {
                                          routeNameRef.current =
                                            navigationRef.current.getCurrentRoute().name;
                                        }}
                                        onStateChange={() => {
                                          // Debounce so route churn during a navigator remount
                                          // collapses into one call fired after the tree settles.
                                          if (screenViewTimeoutRef.current) {
                                            clearTimeout(screenViewTimeoutRef.current);
                                          }
                                          screenViewTimeoutRef.current = setTimeout(async () => {
                                            const currentRouteName =
                                              navigationRef.current?.getCurrentRoute()?.name;
                                            const previousRouteName = routeNameRef.current;

                                            if (
                                              currentRouteName &&
                                              previousRouteName !== currentRouteName
                                            ) {
                                              try {
                                                await analytics().logScreenView({
                                                  screenName: currentRouteName,
                                                  screenClass: 'ReactNativeScreen',
                                                });
                                                await crashlytics().setAttribute(
                                                  'screen',
                                                  currentRouteName,
                                                );
                                              } catch (e) {
                                                console.error(e);
                                              }
                                              routeNameRef.current = currentRouteName;
                                            }
                                          }, 500);
                                        }}
                                        theme={{
                                          ...DefaultTheme,
                                          colors: {
                                            ...DefaultTheme.colors,
                                            background:
                                              colorScheme === 'light' ? '#f6f6f6' : '#0b0b0b',
                                          },
                                        }}
                                      >
                                        <PushNotificationProvider>
                                          <ActionSheetProvider>
                                            <StackProvider>
                                              <MainNavigator />
                                            </StackProvider>
                                          </ActionSheetProvider>
                                        </PushNotificationProvider>
                                      </NavigationContainer>
                                    </>
                                  )}
                                </AdminHelpdeskProvider>
                              </AdminProvider>
                            </HelpdeskProvider>
                          </EventsProvider>
                        </ArticlesProvider>
                      </ExtremeEventProvider>
                    </NotificationProvider>
                  </TagProvider>
                </ModuleProvider>
              </MessageProvider>
            </AuthGuard>
          </AuthProvider>
        </GestureHandlerRootView>
      </ErrorBoundary>
    </SafeAreaProvider>
  );
}
